import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import QuestionManagementMenu from "@/components/admin/QuestionManagementMenu";
import QuestionGeneratorForm from "@/components/admin/QuestionGeneratorForm";
import GeneratedQuestionsList from "@/components/admin/GeneratedQuestionsList";
import { 
  GeneratedQuestion, 
  DifficultyLevel,
  generateQuestions,
  saveQuestions
} from "@/services/questionGeneratorService";

// Don't use lazy loading for now to prevent potential issues
import VideoGenerationModal from "@/components/admin/VideoGenerationModal";

const AdminQuestionGenerator = () => {
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [generationLog, setGenerationLog] = useState<string>("");
  const [generatedQuestions, setGeneratedQuestions] = useState<GeneratedQuestion[]>([]);
  const [selectAll, setSelectAll] = useState<boolean>(true);
  const [showVideoModal, setShowVideoModal] = useState<boolean>(false);
  const [selectedGrade, setSelectedGrade] = useState<string>("");
  const [selectedSubject, setSelectedSubject] = useState<string>("");
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [difficulty, setDifficulty] = useState<DifficultyLevel>("medium");
  const [videoModalKey, setVideoModalKey] = useState<number>(0);
  const [selectedQuestionsForVideo, setSelectedQuestionsForVideo] = useState<GeneratedQuestion[]>([]);
  const [questionListKey, setQuestionListKey] = useState<number>(0); // Added to force re-render of question list

  // Add effect to ensure canvas rendering when questions are updated
  useEffect(() => {
    if (generatedQuestions.length > 0) {
      // Increment the key to force a re-render of the GeneratedQuestionsList component
      setQuestionListKey(prev => prev + 1);
      
      // A short delay to ensure the DOM has updated
      const timer = setTimeout(() => {
        // Try to initialize canvases if the function exists
        if (window.initializeCanvases) {
          console.log("Initializing canvases after questions loaded in AdminQuestionGenerator");
          window.initializeCanvases();
        }
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [generatedQuestions]);

  const handleGenerateQuestions = async (params: {
    grade: string;
    subjectId: string;
    categoryId: string;
    count: number;
    difficulty: DifficultyLevel;
    customPrompt?: string;
    generateWithTopic?: boolean;
  }) => {
    setIsGenerating(true);
    setGenerationLog(`Starting question generation with ${params.difficulty} difficulty...\n`);
    setGeneratedQuestions([]);
    
    setSelectedGrade(params.grade);
    setSelectedSubject(params.subjectId);
    setSelectedCategory(params.categoryId);
    setDifficulty(params.difficulty);

    // Default to topic-based questions if not explicitly specified
    const generateWithTopic = params.generateWithTopic !== undefined ? params.generateWithTopic : true;

    try {
      setGenerationLog(prev => prev + `Generating ${params.count} ${params.difficulty} difficulty ${generateWithTopic ? 'topic-based' : 'standard'} questions...\n`);
      setGenerationLog(prev => prev + `Checking for existing questions to avoid duplicates...\n`);

      const questions = await generateQuestions(
        params.grade,
        params.subjectId,
        params.categoryId,
        params.count,
        params.difficulty,
        params.customPrompt,
        generateWithTopic
      );
      
      if (questions.length === 0) {
        throw new Error("No questions were generated");
      }
      
      setGeneratedQuestions(questions);
      
      const totalGenerated = questions.length;
      const duplicateCount = questions.filter(q => q.isDuplicate).length;
      const uniqueCount = totalGenerated - duplicateCount;
      
      const hasTopic = questions.length > 0 && questions[0].topic;
      
      let logMessage = `Generated ${totalGenerated} ${params.difficulty} difficulty questions (${uniqueCount} unique, ${duplicateCount} duplicates).\n`;
      
      if (hasTopic) {
        logMessage += `Topic: "${questions[0].topic?.title}"\n`;
        console.log("Topic generated:", questions[0].topic);
      } else {
        logMessage += "Standard questions without topic information were generated.\n";
        console.log("No topic information was found in the generated questions.");
      }
      
      logMessage += `Duplicate questions are shown with a warning icon and are not selected by default.\n`;
      logMessage += `You can manually select which questions to save below.\n`;
      
      setGenerationLog(prev => prev + logMessage);
      
      console.log(`Generated ${totalGenerated} questions. ${questions.filter(q => q.selected).length} are selected.`);
      
      toast.success("Success", {
        description: `Generated ${totalGenerated} ${params.difficulty} difficulty questions. Review and select which ones to save.`,
      });
      
      // Call canvas initialization with a delay to ensure DOM is ready
      setTimeout(() => {
        if (window.initializeCanvases) {
          console.log("Initializing canvases after questions generated");
          window.initializeCanvases();
        }
      }, 500);
      
    } catch (error) {
      console.error("Error generating questions:", error);
      setGenerationLog(prev => prev + `Error: ${error.message || "Unknown error occurred"}\n`);
      
      toast.error("Error", {
        description: "Failed to generate questions. Check the logs for details."
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleToggleSelectAll = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);
    
    const updatedQuestions = generatedQuestions.map(q => ({
      ...q,
      selected: newSelectAll
    }));
    
    setGeneratedQuestions(updatedQuestions);
    console.log(`Toggled selection for all questions. ${updatedQuestions.filter(q => q.selected).length} are now selected.`);
  };

  const handleToggleQuestion = (id: string) => {
    const updatedQuestions = generatedQuestions.map(q => 
      q.id === id 
        ? { ...q, selected: !q.selected } 
        : q
    );
    
    setGeneratedQuestions(updatedQuestions);
    
    const areAllSelected = updatedQuestions.every(q => q.selected);
    setSelectAll(areAllSelected);
    
    console.log(`Toggled selection for question ${id}. ${updatedQuestions.filter(q => q.selected).length} questions are now selected.`);
  };

  const handleSetCorrectOption = (questionId: string, optionId: string) => {
    setGeneratedQuestions(questions => 
      questions.map(q => {
        if (q.id === questionId) {
          const updatedOptions = q.options.map(opt => ({
            ...opt,
            isCorrect: opt.id === optionId
          }));
          
          return {
            ...q,
            options: updatedOptions
          };
        }
        return q;
      })
    );

    toast.success("Answer updated", {
      description: "Correct answer has been changed"
    });
  };

  const handleSaveSelectedQuestions = async () => {
    const selectedQuestions = generatedQuestions.filter(q => q.selected);
    
    if (selectedQuestions.length === 0) {
      toast.error("No questions selected", {
        description: "Please select at least one question to save."
      });
      return;
    }

    setIsSaving(true);
    setGenerationLog(prev => prev + `Saving ${selectedQuestions.length} selected ${difficulty} difficulty questions...\n`);

    try {
      const savedCount = await saveQuestions(
        selectedGrade,
        selectedSubject,
        selectedCategory,
        selectedQuestions,
        difficulty
      );
      
      setGenerationLog(prev => prev + `Successfully saved ${savedCount} ${difficulty} difficulty questions to the database.\n`);
      
      toast.success("Success", {
        description: `Saved ${savedCount} ${difficulty} difficulty questions successfully`,
      });
      
      setGeneratedQuestions([]);
    } catch (error) {
      console.error("Error saving questions:", error);
      setGenerationLog(prev => prev + `Error saving questions: ${error.message || "Unknown error occurred"}\n`);
      
      toast.error("Error", {
        description: "Failed to save questions. Check the logs for details."
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenVideoModal = () => {
    try {
      const questionsForVideo = generatedQuestions.filter(q => q.selected);
      
      if (questionsForVideo.length === 0) {
        toast.warning("No questions selected", {
          description: "Please select at least one question to generate a video."
        });
        return;
      }
      
      // CRITICAL FIX: Use JSON.parse/stringify for proper deep cloning
      // instead of structuredClone which may not properly clone all properties
      const questionsClone = JSON.parse(JSON.stringify(questionsForVideo));
      
      console.log(`Opening video modal with ${questionsClone.length} selected questions`);
      questionsClone.forEach((q, idx) => {
        console.log(`Question ${idx+1} for video: ${q.id}, text: ${q.text.substring(0, 30)}...`);
      });
      
      const validatedQuestions = questionsClone.map(q => {
        if (!q.options.some(opt => opt.isCorrect)) {
          q.options[0].isCorrect = true;
          console.log(`Fixed question ${q.id}: No correct option was marked, set first option as correct`);
        }
        else if (q.options.filter(opt => opt.isCorrect).length > 1) {
          const firstCorrectIndex = q.options.findIndex(opt => opt.isCorrect);
          q.options.forEach((opt, index) => {
            opt.isCorrect = index === firstCorrectIndex;
          });
          console.log(`Fixed question ${q.id}: Multiple correct options were marked, keeping only the first one`);
        }
        return q;
      });
      
      // CRITICAL FIX: Force a state update with new reference to trigger proper re-rendering
      // First clear the array, then set it after a short delay to ensure React sees the change
      setSelectedQuestionsForVideo([]);
      
      // Use a more reliable approach with setState callback to ensure proper sequence
      setTimeout(() => {
        console.log(`Setting ${validatedQuestions.length} questions for video modal`);
        setSelectedQuestionsForVideo(validatedQuestions);
        setVideoModalKey(prev => prev + 1);
        setShowVideoModal(true);
      }, 50);
      
    } catch (error) {
      console.error("Error preparing video modal:", error);
      toast.error("Error", {
        description: "Failed to prepare video generation. Please try again."
      });
    }
  };

  const handleUpdateTopic = (updatedTopic: { title: string; content: string }) => {
    if (generatedQuestions.length > 0 && generatedQuestions[0].topic) {
      const updatedQuestions = generatedQuestions.map(q => ({
        ...q,
        topic: updatedTopic
      }));
      
      setGeneratedQuestions(updatedQuestions);
      setGenerationLog(prev => prev + `Updated topic content.\n`);
      
      toast.success("Topic updated", {
        description: "Topic content has been updated for all questions."
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="container mx-auto py-8 px-4">
        <h1 className="text-2xl font-bold mb-4">Admin Dashboard</h1>
        
        <QuestionManagementMenu />
        
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Generate Questions</CardTitle>
          </CardHeader>
          <CardContent>
            <QuestionGeneratorForm
              onGenerateQuestions={handleGenerateQuestions}
              isGenerating={isGenerating}
            />
            
            {generationLog && (
              <div className="mt-4 mb-6">
                <label className="block text-sm font-medium mb-1">Generation Log</label>
                <Textarea
                  value={generationLog}
                  readOnly
                  className="font-mono text-sm h-32"
                />
              </div>
            )}
            
            <GeneratedQuestionsList
              key={questionListKey}
              questions={generatedQuestions}
              selectAll={selectAll}
              onToggleSelectAll={handleToggleSelectAll}
              onToggleQuestion={handleToggleQuestion}
              onSetCorrectOption={handleSetCorrectOption}
              onSaveQuestions={handleSaveSelectedQuestions}
              onShowVideoModal={handleOpenVideoModal}
              isSaving={isSaving}
              topic={generatedQuestions[0]?.topic}
              onUpdateTopic={handleUpdateTopic}
            />
          </CardContent>
        </Card>
        
        {showVideoModal && (
          <VideoGenerationModal 
            key={videoModalKey}
            questions={selectedQuestionsForVideo}
            open={showVideoModal}
            onClose={() => setShowVideoModal(false)}
          />
        )}
      </main>
    </div>
  );
};

export default AdminQuestionGenerator;
